import { isPlatformBrowser } from '@angular/common';
import type { EnvironmentProviders } from '@angular/core';
import { DestroyRef, ErrorHandler, PLATFORM_ID, Service, inject, makeEnvironmentProviders, provideAppInitializer } from '@angular/core';
import { Router } from '@angular/router';

import type { ErrorOrigin } from '@app/core/errors/error-report.types';
import { toErrorReport } from '@app/core/errors/error-report.util';
import { ErrorReporter } from '@app/core/errors/error-reporter';

@Service()
export class GlobalErrorHandler implements ErrorHandler {
  private readonly reporter = inject(ErrorReporter);
  private readonly router = inject(Router);

  public handleError(error: unknown): void {
    this.reportEscaped(error, 'angular');
  }

  public handleUnhandledRejection(reason: unknown): void {
    this.reportEscaped(reason, 'unhandled-rejection');
  }

  public handleUncaught(failure: unknown): void {
    this.reportEscaped(failure, 'uncaught');
  }

  private reportEscaped(error: unknown, origin: ErrorOrigin): void {
    this.reporter.report(toErrorReport(error, this.router.url, origin));
  }
}

export function listenToWhatEscapes(): void {
  if (!isPlatformBrowser(inject(PLATFORM_ID))) {
    return;
  }

  const handler = inject(GlobalErrorHandler);
  const destroyRef = inject(DestroyRef);
  const onRejection = (event: PromiseRejectionEvent): void => handler.handleUnhandledRejection(event.reason as unknown);
  const onUncaught = (event: ErrorEvent): void => handler.handleUncaught((event.error ?? event.message) as unknown);

  window.addEventListener('unhandledrejection', onRejection);
  window.addEventListener('error', onUncaught);

  destroyRef.onDestroy(() => {
    window.removeEventListener('unhandledrejection', onRejection);
    window.removeEventListener('error', onUncaught);
  });
}

export function provideGlobalErrorHandler(): EnvironmentProviders {
  return makeEnvironmentProviders([{ provide: ErrorHandler, useExisting: GlobalErrorHandler }, provideAppInitializer(listenToWhatEscapes)]);
}
